import DSBox from "views/system-design/DSBox";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSChip from "views/system-design/DSChip";
import DSSectionHeading from "views/system-design/DSSectionHeading";
import DSTypography from "views/system-design/DSTypography";
import { sectionCardSx } from "views/system-design/sectionCardSx";
import { settingByField } from "../../Device/syncedSettings";

// Keys the device modal shows under another name, or that aren't synced
// settings in syncedSettings.ts.
const EXTRA_LABELS: Record<string, string> = {
  defaultCustomChimeId: "Default Chime",
  isPreHandledEditionClock: "Pre-Handled Edition",
  skippedRegistration: "Skipped Registration",
  // Removed from the firmware; older profiles may still store them.
  sort: "Sort Type (unused)",
  sortDown: "Sort Down (unused)",
};

const labelOf = (field: string) =>
  settingByField(field)?.label ?? EXTRA_LABELS[field] ?? field;

interface ManagedSettingsCardProps {
  values: Record<string, unknown>;
  // Missing on a ready profile, which can't drop a setting anymore.
  onUnset?: (field: string) => void;
}

/**
 * The settings this profile pushes. A setting is pushed from the moment it
 * is changed below, and the switches and dropdowns can only change a value,
 * never drop it — removing it here is how a profile stops managing a setting
 * and leaves each device's own value alone on the next Sync.
 */
const ManagedSettingsCard = ({ values, onUnset }: ManagedSettingsCardProps) => {
  const fields = Object.keys(values).sort((a, b) =>
    labelOf(a).localeCompare(labelOf(b)),
  );

  return (
    <DSCard sx={[sectionCardSx, { mb: 2 }]}>
      <DSSectionHeading>Pushed settings</DSSectionHeading>
      <DSCardContent>
        {fields.length ? (
          <>
            <DSTypography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Sync overwrites these on every device. Remove one to leave it as
              it is on each device.
            </DSTypography>
            <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {fields.map((field) => (
                <DSChip
                  key={field}
                  size="small"
                  label={labelOf(field)}
                  onDelete={onUnset && (() => onUnset(field))}
                />
              ))}
            </DSBox>
          </>
        ) : (
          <DSTypography variant="body2" color="text.secondary">
            None yet — a setting is pushed once you change it below.
          </DSTypography>
        )}
      </DSCardContent>
    </DSCard>
  );
};

export default ManagedSettingsCard;
